/** Nachrichtenbereich eines Kanals: Liste, Tipp-Anzeige, Composer, Drag & Drop für Anhänge, Dialoge. */
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useAppStore } from '../../store/app';
import { Composer } from './Composer';
import { queueFiles } from './files';
import { MessageDialogs } from './MessageDialogs';
import { MessageList } from './MessageList';
import { useMessagePerms } from './perms';
import { TypingIndicator } from './TypingIndicator';
import './messages.css';

export interface MessagePaneProps {
  channelId: string;
  /** Im Thread-Seitenpanel: kompaktere Darstellung. */
  variant?: 'main' | 'panel';
  /** Zu dieser Nachricht springen (aus `?message=`), danach `onJumped` aufrufen. */
  jumpToMessageId?: string | null;
  onJumped?: () => void;
}

const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

export function MessagePane({ channelId, variant = 'main', jumpToMessageId, onJumped }: MessagePaneProps) {
  const channel = useAppStore((s) => s.channels[channelId]);
  const perms = useMessagePerms(channelId);
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const panel = variant === 'panel';

  if (!channel) {
    return (
      <div className="message-pane empty" data-testid="message-pane" data-channel-id={channelId}>
        <div className="spinner" />
      </div>
    );
  }

  const canDrop = perms.canSend && perms.canAttach;
  return (
    <div
      className={`message-pane ${panel ? 'panel' : ''}`}
      data-testid="message-pane"
      data-channel-id={channelId}
      data-variant={variant}
      onDragEnter={(e) => {
        if (!canDrop || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => {
        if (!canDrop || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={() => {
        if (!dragging) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      }}
      onDrop={(e) => {
        if (!canDrop || !hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        queueFiles(channelId, e.dataTransfer.files, perms.canAttach);
      }}
    >
      <MessageList key={channelId} channel={channel} perms={perms} panel={panel} jumpToMessageId={jumpToMessageId} onJumped={onJumped} />
      <div className="message-pane-bottom">
        <Composer key={channelId} channel={channel} perms={perms} panel={panel} />
        <TypingIndicator channelId={channelId} serverId={channel.serverId} />
      </div>
      {dragging && (
        <div className="drop-overlay" data-testid="drop-overlay" aria-hidden="true">
          <div className="drop-overlay-inner">
            <Upload size={40} />
            <strong>Dateien hier ablegen</strong>
            <span className="small">Sie werden an deine nächste Nachricht angehängt.</span>
          </div>
        </div>
      )}
      <MessageDialogs channelId={channelId} />
    </div>
  );
}
